import React from 'react';
import './MarketplacePracticeProfile.scss';
import MarketplacePracticeLocationsTable from './MarketplacePracticeLocationsTable/MarketplacePracticeLocationsTable';
import PracticeProfileImages from '../../PracticeProfileImages/PracticeProfileImages';
import { Input } from '@availity/form';
import { Col, FormGroup, Row } from 'reactstrap';
import {
    OwnershipType,
    OwnershipTypeOptions,
} from '../../../slices/marketplace/timeline/models/enums/ownership-type.enum';
import {
    RevenueGrowthRate,
    RevenueGrowthRates,
} from '../../../slices/marketplace/timeline/models/enums/revenue-growth.enum';
import { PracticeProfileInfo } from '../../../slices/profiles-data-slice/models/practice-profile-info';
import { useFormikContext, getIn } from 'formik';
import {
    DataSynchronizationValidator,
    SyncField,
} from '../../DataSynchronizationValidator/DataSynchronizationValidator';
import { useServiceFocuses } from '../../../custom-hooks/useServiceFocuses';
import {
    isOtherFocus,
} from '../../../containers/UserPages/Login/Modals/PracticeRegistrationForm/utils';

interface MarketplacePracticeProfileProps {
    practice: PracticeProfileInfo;
    profileId: string;
    fieldPrefix: string;
    setFormFieldValue(name: string, value: any): void;
    onAddLocationClicked(): void;
    removeLocation(locationIndex: number): void;
    isEditable?: boolean;
    dataSyncTrigger?: number;
}

export const MarketplacePracticeProfile = (
    props: MarketplacePracticeProfileProps
) => {
    const { isEditable = true } = props;
    const practice = props.practice;
    const serviceFocuses = useServiceFocuses();
    const { values } = useFormikContext();

    const isOtherPracticeFocusInputVisible = () => {
        return isOtherFocus(
            serviceFocuses,
            getIn(values, `${props.fieldPrefix}.practiceFocusType`)
        );
    };

    const renderDataSynchronizationValidator = () => {
        const currentPractice = getIn(values, props.fieldPrefix);
        const practiceId = currentPractice?.practiceId || practice.practiceId;
        const fteDvms = currentPractice?.fteDvms || practice.fteDvms || 0;

        if (!practiceId) {
            return null;
        }

        return (
            <DataSynchronizationValidator
                key={`practice-sync-validator-${practice.id}`}
                practiceId={practiceId}
                fteDvm={fteDvms}
                ltmRevenue={0}
                sync={props.dataSyncTrigger}
                fieldsToSync={[SyncField.FteDvm]}
            />
        );
    };

    return (
        <div className="marketplace-practice-profile">
            <section className="practice-information">
                <div className="practice-form-container">
                    <Row>
                        <Col md={6} className="practice-form">
                            <div className="section-title mb-4">
                                <h2>Practice Information</h2>
                            </div>
                            <FormGroup>
                                <Row className="horizontal-form-group">
                                    <Col
                                        md={3}
                                        className="col-form-label"
                                        tag="label">
                                        Practice Name
                                    </Col>
                                    <Col md={7}>
                                        <Input
                                            name={`${props.fieldPrefix}.practiceName`}
                                            type="text"
                                            className="form-control"
                                            disabled={!isEditable}
                                        />
                                    </Col>
                                </Row>
                            </FormGroup>

                            <FormGroup>
                                <Row className="horizontal-form-group">
                                    <Col
                                        md={3}
                                        className="col-form-label"
                                        tag="label">
                                        Owner Name
                                    </Col>
                                    <Col md={7}>
                                        <Input
                                            name={`${props.fieldPrefix}.ownerFullName`}
                                            type="text"
                                            className="form-control"
                                            disabled={!isEditable}
                                        />
                                    </Col>
                                </Row>
                            </FormGroup>

                            <FormGroup>
                                <Row className="horizontal-form-group">
                                    <Col
                                        md={3}
                                        className="col-form-label"
                                        tag="label">
                                        Ownership Type
                                    </Col>
                                    <Col md={7}>
                                        <Input
                                            name={`${props.fieldPrefix}.practiceOwnershipType`}
                                            type="select"
                                            className="form-control"
                                            disabled={!isEditable}
                                            defaultValue={
                                                OwnershipType.SingleOwner
                                            }>
                                            {Object.entries(
                                                OwnershipTypeOptions
                                            ).map(([key, value]) => (
                                                <option key={key} value={key}>
                                                    {value}
                                                </option>
                                            ))}
                                        </Input>
                                    </Col>
                                </Row>
                            </FormGroup>

                            <FormGroup>
                                <Row className="horizontal-form-group">
                                    <Col
                                        md={3}
                                        className="col-form-label"
                                        tag="label">
                                        Service Focus
                                    </Col>
                                    <Col md={7}>
                                        <Input
                                            name={`${props.fieldPrefix}.practiceFocusType`}
                                            type="select"
                                            className="form-control"
                                            disabled={!isEditable}
                                            defaultValue={1}>
                                            {serviceFocuses.map((option) => (
                                                <option
                                                    key={option.id}
                                                    value={option.id}>
                                                    {option.name}
                                                </option>
                                            ))}
                                        </Input>
                                    </Col>
                                </Row>
                            </FormGroup>

                            <FormGroup
                                hidden={!isOtherPracticeFocusInputVisible()}>
                                <Row>
                                    <Col
                                        md={3}
                                        className="col-form-label"
                                        tag="label">
                                        Specify Service Focus
                                    </Col>
                                    <Col md={7}>
                                        <Input
                                            name={`${props.fieldPrefix}.otherPracticeType`}
                                            type="text"
                                            className="form-control"
                                            disabled={!isEditable}
                                        />
                                    </Col>
                                </Row>
                            </FormGroup>

                            <FormGroup>
                                <Row className="horizontal-form-group">
                                    <Col
                                        md={3}
                                        className="col-form-label"
                                        tag="label">
                                        Number of FTE DVMs
                                    </Col>
                                    <Col md={7}>
                                        <Input
                                            name={`${props.fieldPrefix}.fteDvms`}
                                            type="number"
                                            className="form-control"
                                            readOnly={true}
                                            disabled={!isEditable}
                                            defaultValue={0}
                                        />
                                    </Col>
                                </Row>
                            </FormGroup>

                            <FormGroup>
                                <Row className="horizontal-form-group">
                                    <Col
                                        md={3}
                                        className="col-form-label"
                                        tag="label">
                                        Revenue Growth Rate
                                    </Col>
                                    <Col md={7}>
                                        <Input
                                            name={`${props.fieldPrefix}.revenueGrowthRate`}
                                            type="select"
                                            className="form-control"
                                            disabled={!isEditable}
                                            defaultValue={
                                                RevenueGrowthRate.noGrowth
                                            }>
                                            {RevenueGrowthRates.map(
                                                (option) => (
                                                    <option
                                                        key={option.id}
                                                        value={option.id}>
                                                        {option.name}
                                                    </option>
                                                )
                                            )}
                                        </Input>
                                    </Col>
                                </Row>
                            </FormGroup>
                        </Col>
                        <Col md={6} className="practice-form">
                            <div className="section-title mb-4">
                                <h2>Practice Images</h2>
                            </div>
                            <PracticeProfileImages
                                key={`${practice.id}-${
                                    (practice.uploads || []).length
                                }`}
                                images={practice.uploads || []}
                                profileId={props.profileId}
                                practiceProfileId={practice.id}
                                maxImages={10}
                                disabled={!isEditable}
                            />
                        </Col>
                    </Row>

                    <Row>
                        <Col>
                            <FormGroup className="description-container">
                                <label>Description</label>
                                <Input
                                    name={`${props.fieldPrefix}.description`}
                                    type="textarea"
                                    rows={8}
                                    className="form-control"
                                    disabled={!isEditable}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                </div>
            </section>

            {practice.practiceLocations?.length ? (
                <MarketplacePracticeLocationsTable
                    locations={practice.practiceLocations}
                    fieldPrefix={`${props.fieldPrefix}.practiceLocations`}
                    setFormFieldValue={props.setFormFieldValue}
                    removeLocation={props.removeLocation}
                    isEditable={isEditable}
                />
            ) : (
                <></>
            )}
            {isEditable && (
                <div
                    className="link-button"
                    onClick={props.onAddLocationClicked}>
                    {practice.practiceLocations?.length
                        ? '+ Add Another Location'
                        : '+ Add Location'}
                </div>
            )}
            {renderDataSynchronizationValidator()}
        </div>
    );
};
